import 'swiper/css';
import 'swiper/css/scrollbar';
import 'swiper/css/navigation';

import React, { Fragment, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import MyHead from '../../components/MyHead';
import Image from 'next/future/image';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Scrollbar } from 'swiper';

import { buttonVariants } from '@/components/v2/UI/Button';

import LinkTo from '@/components/LinkTo';
import { GetServerSidePropsContext, InferGetServerSidePropsType } from 'next';
import { getRealLangCountry } from '@/helpers/languages';
import { fetchProductsBySellingCodes } from '@/fetchers/products';

import ProductCard from '@/components/v2/Pages/Category/Products/ProductCard';

const AirnestPage = ({
  products
}: InferGetServerSidePropsType<typeof getServerSideProps>) => {
  const { t } = useTranslation();

  const cardsOne = useMemo(
    () => [
      {
        title: t('P_AIRNEST.CARDS.CARD-1.TITLE'),
        description: t('P_AIRNEST.CARDS.CARD-1.DESC'),
        icosrc: '/v2/sustainability/icon-secondlife.png',
        link: '/second-life',
        btntext: t('P_AIRNEST.CARDS.CARD-1.BTN'),
        src: '/v2/airnest/box-second-life.jpg'
      },
      {
        title: t('P_AIRNEST.CARDS.CARD-2.TITLE'),
        description: t('P_AIRNEST.CARDS.CARD-2.DESC'),
        icosrc: '/v2/sustainability/icon-biomaterials.png',
        link: '/sustainability',
        btntext: t('P_AIRNEST.CARDS.CARD-2.BTN'),
        src: '/v2/airnest/box-biomaterials.jpg'
      }
    ],
    []
  );

  return (
    <Fragment>
      <MyHead />

      <div className="section relative full bg-[#D4D5CF]">
        <div className="section-wrapper relative ">
          <div className="grid gap-8 grid-cols-12 md:gap-12 xl:gap-20">
            <div className="md:col-span-8 col-span-12">
              <div className="lead-1 text-[#A59B92]">
                {t('P_AIRNEST.SECTION-1.TITLE-1')}
              </div>
            </div>
            <div className="md:col-span-3 self-end md:col-start-9 col-span-6 col-start-4">
              <Image
                src="/v2/airnest/logo-airnest.png"
                alt=""
                width={0}
                height={0}
                priority
                sizes="100vw"
                className="w-full"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="section relative full bg-[#D4D5CF]">
        <div className="section-wrapper relative ">
          <div className="grid gap-8 grid-cols-12 md:gap-12 xl:gap-20">
            <div className="md:col-span-5 col-span-12 self-center">
              <div className="h3">{t('P_AIRNEST.SECTION-1.TITLE-2')}</div>
              <div className="tracking-uyn md:text-xl opacity-80 mt-6">
                {t('P_AIRNEST.SECTION-1.DESC-2')}
              </div>
            </div>
            <div className="md:col-span-7 col-span-12">
              <Image
                src="/v2/airnest/airnest-1.jpg"
                alt=""
                width={0}
                height={0}
                sizes="100vw"
                className="w-full"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="section relative full bg-[#D4D5CF]">
        <div className="section-wrapper relative ">
          <div className="grid gap-8 grid-cols-12 md:gap-12 xl:gap-20">
            <div className="md:col-span-7 order-2 md:order-1 col-span-12">
              <Image
                src="/v2/airnest/airnest-2.jpg"
                alt=""
                width={0}
                height={0}
                sizes="100vw"
                className="w-full"
              />
            </div>
            <div className="md:col-span-5 col-span-12 self-center space-y-16 order-1 md:order-2">
              <div>
                <div className="h3">{t('P_AIRNEST.SECTION-1.TITLE-3')}</div>
                <div className="tracking-uyn md:text-xl opacity-80 mt-6">
                  {t('P_AIRNEST.SECTION-1.DESC-3')}
                </div>
              </div>
              <div>
                <div className="h3">{t('P_AIRNEST.SECTION-1.TITLE-4')}</div>
                <div className="tracking-uyn md:text-xl opacity-80 mt-6">
                  {t('P_AIRNEST.SECTION-1.DESC-4')}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="section relative full bg-gray-900 text-white overflow-hidden">
        <Image
          src="/v2/airnest/airnest-3.jpg"
          alt=""
          width={0}
          height={0}
          sizes="100vw"
          className="absolute w-full h-full object-cover object-center top-0 left-0 z-10"
        />

        <div className="absolute w-full h-full bg-black/30 z-20 top-0 left-0"></div>

        <div className="section-wrapper relative z-30">
          <div className="grid gap-8 grid-cols-12 md:gap-12 xl:gap-20">
            <div className="md:col-span-7 col-span-12">
              <div className="h2">{t('P_AIRNEST.SECTION-2.LEFT')}</div>
            </div>
            <div className="md:col-span-5 col-span-12 self-center space-y-10">
              <div>
                <div className="lead-1">{t('P_AIRNEST.SECTION-2.LEAD-1')}</div>
                <div className="text-body mt-3">
                  {t('P_AIRNEST.SECTION-2.DESC-1')}
                </div>
              </div>
              <div>
                <div className="lead-1">{t('P_AIRNEST.SECTION-2.LEAD-2')}</div>
                <div className="text-body mt-3">
                  {t('P_AIRNEST.SECTION-2.DESC-2')}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="section relative full bg-[#D4D5CF]">
        <div className="section-wrapper relative ">
          <div className="grid gap-8 grid-cols-12 md:gap-12 xl:gap-20">
            <div className="md:col-span-6 col-span-12 order-2 md:order-1">
              <Image
                src="/v2/airnest/airnest-4.jpg"
                alt=""
                width={0}
                height={0}
                sizes="100vw"
                className="w-full max-w-[70%]"
              />
              <Image
                src="/v2/airnest/airnest-5.jpg"
                alt=""
                width={0}
                height={0}
                sizes="100vw"
                className="w-full ml-auto mr-[5%] mt-[-35%] max-w-[60%]"
              />
            </div>
            <div className="md:col-span-5 col-span-12 self-center space-y-16 order-1 md:order-2">
              <div>
                {/* <div className="h3">{t('P_AIRNEST.SECTION-1.TITLE-3')}</div> */}
                <div className="tracking-uyn md:text-xl opacity-80 mt-6">
                  {t('P_AIRNEST.SECTION-1.DESC-3')}
                </div>
              </div>
              {/* <div>
                <div className="h3">{t('P_AIRNEST.SECTION-1.TITLE-4')}</div>
                <div className="tracking-uyn md:text-xl opacity-80 mt-6">
                  {t('P_AIRNEST.SECTION-1.DESC-4')}
                </div>
              </div> */}
            </div>
          </div>
        </div>
      </div>

      <div className="section relative full">
        <div className="section-wrapper mb-12">
          <div className="h3">{t('P_AIRNEST.SECTION-4.TITLE')}</div>
        </div>
        <Swiper
          wrapperTag="ul"
          slidesPerView={1.2}
          spaceBetween={16}
          navigation={true}
          modules={[Navigation]}
          className="px-6 md:px-10 xl:px-16"
          breakpoints={{
            768: {
              slidesPerView: 2.2
            },
            1024: {
              slidesPerView: 3.2
            },
            1280: {
              slidesPerView: 4.2
            }
          }}
        >
          {products.map(product => (
            <SwiperSlide tag="li" key={product.name}>
              <ProductCard data={product} />
            </SwiperSlide>
          ))}
        </Swiper>
      </div>

      <div className="section relative full">
        <div className="section-wrapper relative">
          <Swiper
            wrapperTag="ul"
            slidesPerView={1.2}
            spaceBetween={16}
            breakpoints={{
              768: {
                slidesPerView: 2
              }
            }}
          >
            {cardsOne.map((card, idx) => (
              <SwiperSlide
                key={idx}
                tag="li"
                className="rounded-uyn overflow-hidden relative md:aspect-card aspect-cardSwiper group"
              >
                <Image
                  src={card.src}
                  alt=""
                  width={0}
                  height={0}
                  sizes="100vw"
                  className="absolute w-full h-full object-cover object-center"
                />

                <div className="absolute z-10 w-full h-full left-0 top-0 bg-black/40 group-hover:bg-black/50 transition-all" />
                <div className="absolute z-20 inset-0 flex flex-col text-white p-6 md:p-8">
                  <Image
                    src={card.icosrc}
                    alt=""
                    width={0}
                    height={0}
                    sizes="100vw"
                    className="w-full max-w-[120px] h-auto"
                  />

                  <div className="mt-[25%]">
                    <h3 className="h4">{card.title}</h3>
                    <p className="text-body">{card.description}</p>
                  </div>

                  <div className="flex gap-2 flex-wrap mt-auto">
                    <LinkTo
                      href={card.link}
                      aria-label={card.btntext}
                      className={buttonVariants({ variant: 'light' })}
                    >
                      {card.btntext}
                    </LinkTo>
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </div>
    </Fragment>
  );
};

export async function getServerSideProps(ctx: GetServerSidePropsContext) {
  const token = ctx.req.cookies['uyn_bearer'];

  const products = await fetchProductsBySellingCodes(
    ['O102163', 'O102164', 'O102792', 'O102793', 'O102796', 'O102797'],
    {
      token,
      ...getRealLangCountry(ctx.params)
    }
  );

  return {
    props: {
      products
    }
  };
}

export default AirnestPage;
